import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  TableroIcon,
  FacturacionIcon,
  HorariosIcon,
  PrestamosIcon,
  SolicitudesIcon,
  UsuariosIcon,
} from './SidebarIcons';
import { useAuth } from '../../context/AuthContext';
import BrandLogo from './BrandLogo';
import { APP_RUN_LABEL, APP_VERSION_LABEL, IS_STAGING } from '../../config/appInfo';
import StageRibbon from '../StageRibbon';

const Sidebar = ({ isOpen, onToggle, isMobile, onCloseMobile }) => {
  const { currentUser, isAdmin, isManager, isTechnician } = useAuth();
  const showSidebarFooterText = isOpen || isMobile;

  const navItems = [
    { to: '/', icon: TableroIcon, label: 'Tablero', permission: 'dashboard' },
    { to: '/invoices', icon: FacturacionIcon, label: 'Facturación', permission: 'invoices' },
    { to: '/schedules', icon: HorariosIcon, label: 'Horarios', permission: 'schedules' },
    { to: '/loans', icon: PrestamosIcon, label: 'Préstamos', permission: 'loans' },
    { to: '/requests', icon: SolicitudesIcon, label: 'Solicitudes', permission: 'tasks' },
    { to: '/team', icon: UsuariosIcon, label: 'Usuarios', permission: 'team' },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (isAdmin) return true;
    const userPermissions = currentUser?.permissions || [];
    if (item.permission === 'team') return isManager || userPermissions.includes('team');
    if (item.permission === 'tasks' && isTechnician) return true;
    return userPermissions.includes(item.permission);
  });

  const handleNavClick = () => {
    if (isMobile) onCloseMobile();
  };

  return (
    <aside
      data-testid="sidebar"
      className={`
        fixed inset-y-0 left-0 z-40
        bg-sidebar-background text-sidebar-foreground
        flex h-[100dvh] flex-col
        transition-all duration-300 ease-in-out
        ${isMobile ? 'w-60' : (isOpen ? 'w-52' : 'w-16')}
        ${isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}
        shadow-xl
      `}
    >
      <div className={`border-b border-slate-800 px-3 py-2 ${isOpen || isMobile ? 'space-y-1' : ''}`}>
        <div className="flex items-center justify-center overflow-hidden py-1">
          {isOpen || isMobile ? (
            <BrandLogo variant="dark" className="h-20 w-auto max-w-[210px] scale-125 transform-gpu" />
          ) : (
            <BrandLogo compact variant="dark" className="h-10 w-10" />
          )}
        </div>

        {(isOpen || isMobile) && (
          <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
            CRM operativo
          </p>
        )}
      </div>

      <nav className="hide-scrollbar flex-1 space-y-1.5 overflow-y-auto px-2 py-4">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={handleNavClick}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `group relative flex items-center gap-2.5 rounded-lg px-3 py-2.5 transition-all duration-200
              ${isActive
                ? 'bg-primary text-white shadow-lg shadow-primary/30'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
              }
              ${!isOpen && !isMobile ? 'justify-center px-2' : ''}
              `
            }
          >
            <item.icon size={19} strokeWidth={1.8} className="flex-shrink-0" />
            {(isOpen || isMobile) && (
              <span className="text-[13px] font-medium">{item.label}</span>
            )}

            {!isOpen && !isMobile && (
              <div className="absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2 text-sm text-white opacity-0 invisible shadow-xl transition-all group-hover:visible group-hover:opacity-100">
                {item.label}
                <div className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1 -translate-y-1/2 rotate-45 bg-slate-900"></div>
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      <div className={`relative flex items-center justify-center overflow-hidden border-t border-slate-800 px-4 ${IS_STAGING ? 'min-h-24 py-5' : 'py-3'}`}>
        <StageRibbon compact={!isOpen && !isMobile} />
        {showSidebarFooterText && (
          <div
            className="relative z-20 text-center leading-tight"
            title={`${APP_VERSION_LABEL} · ${APP_RUN_LABEL}`}
          >
            <div className="text-[11px] font-semibold text-slate-400">
              {APP_VERSION_LABEL}
            </div>
            <div className="mt-1 text-[9px] font-medium uppercase tracking-[0.14em] text-slate-600">
              {APP_RUN_LABEL}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
